import React, { type ReactNode } from 'react';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { FeedbackSentiment, sentimentMap } from '@/types/feedbackConst';
import { orderedSentiment } from '@/types/sentimentConst';

import RawTextSentimentItem from './RawTextSentimentItem';

type Props = {
  children: ReactNode;
};

const SentimentInfo = ({ children }: Props) => {
  return (
    <StyledSentimentInfo>
      {React.Children.map(children, (child, index) => (
        <div key={index}>
          {child}
          {index !== React.Children.count(children) - 1 && <Space h={24} />}
        </div>
      ))}
    </StyledSentimentInfo>
  );
};

const SentimentColor = () => {
  const color = useColor();

  return (
    <>
      <VFlex g={6}>
        <Typo type="label1" weight="sb" color={color.gray800}>
          Sentiment color
        </Typo>
        <Typo type="body2" weight="r" color={color.gray700}>
          Sentiments are represented by {orderedSentiment.length} distinct color
          variations.
        </Typo>
      </VFlex>
      <Space h={16} />
      <VFlex g={12}>
        {orderedSentiment.map((key) => {
          const sentiment = sentimentMap[key];
          return (
            <Flex key={key} a="center" g={12}>
              <Flex
                style={{
                  width: 18,
                  aspectRatio: 1,
                  borderRadius: 10,
                  backgroundColor: color.sentiment[key],
                }}
              ></Flex>
              <Flex
                g="8px"
                a="center"
                padding="4px 8px"
                style={{
                  border: `1px solid ${color.gray300}`,
                  borderRadius: 8,
                }}
              >
                {sentiment.icon}
                <MainText dim>{sentiment.value}</MainText>
              </Flex>
            </Flex>
          );
        })}
      </VFlex>
    </>
  );
};

const KeySentiment = () => {
  const color = useColor();

  return (
    <>
      <VFlex g={6}>
        <Typo type="label1" weight="sb" color={color.gray800}>
          Key sentiment
        </Typo>
        <Typo type="body2" weight="r" color={color.gray700}>
          Syncly highlights messages with the highest sentiment magnitude while
          prioritizing negative sentiment first. Sentiments expressed in these
          messages are considered key sentiment, hence highlighted with an
          exclamation icon and colored accordingly.
        </Typo>
      </VFlex>
      <Space h={12} />
      <RawTextSentimentItem
        sentence={{
          source_id: 'message',
          primary: true,
          text: 'message',
          sentiment: FeedbackSentiment.NEGATIVE,
        }}
      />
      <Space h={12} />
      <RawTextSentimentItem
        sentence={{
          source_id: 'message',
          primary: true,
          text: 'message',
          sentiment: FeedbackSentiment.POSITIVE,
        }}
      />
    </>
  );
};

SentimentInfo.SentimentColor = SentimentColor;
SentimentInfo.KeySentiment = KeySentiment;

const StyledSentimentInfo = styled(VFlex)`
  width: 400px;
  padding: 24px;
`;

export default SentimentInfo;
